/**
 * Format currency with decimal places and precision 2
 * @param value - The value to format
 * @returns Formatted currency string (e.g., "Rp 10,000")
 */
export const formatterCurrency = (value?: string | number) => {
  if (!value) {
    return "";
  }

  const stringValue = `${value}`;

  // Handle decimal numbers with comma (already formatted)
  if (stringValue.includes(",")) {
    const [integerPart, decimalPart] = stringValue.split(",");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${formattedInteger},${decimalPart}`;
  }

  // Handle decimal numbers with dot (convert to comma format)
  if (stringValue.includes(".")) {
    const [integerPart, decimalPart] = stringValue.split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${formattedInteger},${decimalPart}`;
  }

  return stringValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

/**
 * Parse currency string to number
 * @param value - The value to parse
 * @returns Parsed number
 */
export const parserCurrency = (value?: string) => {
  if (!value) {
    return "";
  }

  // Keep digits, comma, and dots (for thousands separator)
  const cleaned = value.replace(/[^\d,.]/g, "");

  // Handle comma as decimal separator
  if (cleaned.includes(",")) {
    const [integerPart, decimalPart] = cleaned.split(",");
    const cleanInteger = integerPart.replace(/\./g, "");
    return parseFloat(`${cleanInteger}.${decimalPart}`) || 0;
  }

  // Handle dots as thousands separators for integers
  const numericValue = cleaned.replace(/\./g, "");

  return parseFloat(numericValue) || 0;
};

/**
 * Format quantity with decimal places and precision 2
 * @param value - The value to format
 * @returns Formatted quantity string (e.g., "10,00")
 */
export const formatterQuantity = (value?: string | number) => {
  if (!value) {
    return "";
  }

  const stringValue = value.toString();

  // Handle comma format (already formatted)
  if (stringValue.includes(",")) {
    const [integerPart, decimalPart] = stringValue.split(",");

    if (decimalPart === "00") {
      return integerPart;
    }

    return `${integerPart},${decimalPart}`;
  }

  // Handle dot format (from number conversion)
  if (stringValue.includes(".")) {
    const [integerPart, decimalPart] = stringValue.split(".");

    if (decimalPart === "00") {
      return integerPart;
    }

    return `${integerPart},${decimalPart}`;
  }

  return stringValue;
};

/**
 * Parse quantity string to number
 * @param value - The value to parse
 * @returns Parsed number
 */
export const parserQuantity = (value?: string) => {
  if (!value) {
    return "";
  }

  if (value.includes(",")) {
    const [integerPart, decimalPart] = value.split(",");

    if (decimalPart === "00") {
      return parseFloat(integerPart) || 0;
    }

    return parseFloat(`${integerPart}.${decimalPart}`) || 0;
  }

  return parseFloat(value) || 0;
};

/**
 * Handle key down event for input number
 * @param e - The keyboard event
 */
export const onKeyDownInputNumber = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  // Allow digits
  if (/[0-9]/.test(e.key)) {
    return;
  }

  // Allow comma for decimal separator (only one comma allowed)
  if (e.key === "," && !e.currentTarget.value.includes(",")) {
    return;
  }

  // Allow all command/control keys (navigation, function keys, etc.)
  if (e.key.length > 1) {
    return;
  }

  // Allow keyboard shortcuts (Ctrl/Cmd + key combinations)
  if (e.ctrlKey || e.metaKey) {
    return;
  }

  // Allow all other keys except alphabetic characters and special characters (except comma)
  if (
    !/[a-zA-Z]/.test(e.key) &&
    !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(e.key)
  ) {
    return;
  }

  e.preventDefault();
};
